import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import Pagination from '../components/Pagination';
import { searchFieldsMatch } from '../utils/searchText';

const OrganizationsList = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { confirm } = useConfirmDialog();
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    filterOrganizations();
  }, [organizations, searchQuery, selectedStatus]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data || []);
    } catch (err) {
      showError('Erreur lors du chargement des organisations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterOrganizations = () => {
    let filtered = organizations;

    // Filtrer par recherche
    if (searchQuery.trim()) {
      filtered = filtered.filter((org) =>
        searchFieldsMatch(searchQuery, [org.name, org.subdomain, org.email, org.phone])
      );
    }

    // Filtrer par statut
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(org => 
        selectedStatus === 'active' ? org.is_active : !org.is_active
      );
    }

    setFilteredOrganizations(filtered);
    setCurrentPage(1); // Reset à la première page lors du filtrage
  };

  const handleDelete = async (orgId, orgName) => {
    const confirmed = await confirm({
      title: 'Supprimer l\'organisation',
      message: `Êtes-vous sûr de vouloir supprimer l'organisation "${orgName}" ? Cette action est irréversible.`,
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await organizationsAPI.delete(orgId);
      success('Organisation supprimée avec succès');
      loadOrganizations();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (orgId, currentStatus, orgName) => {
    const action = currentStatus ? 'désactiver' : 'activer';
    const confirmed = await confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} l'organisation`,
      message: `Êtes-vous sûr de vouloir ${action} l'organisation "${orgName}" ?`,
      type: currentStatus ? 'warning' : 'info'
    });

    if (!confirmed) return;

    try {
      await organizationsAPI.update(orgId, { is_active: !currentStatus });
      success(`Organisation ${currentStatus ? 'désactivée' : 'activée'} avec succès`);
      loadOrganizations();
    } catch (err) {
      showError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // Pagination (liste filtrée côté client)
  const listTotal = filteredOrganizations.length;
  const totalPages = Math.max(1, Math.ceil(listTotal / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, startIndex + itemsPerPage);

  const renderOrgPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={listTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(p) => {
        setCurrentPage(p);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setCurrentPage(1);
      }}
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des organisations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Uruganda Orgs - Administration"
        description="Gérez toutes les organisations de la plateforme"
      />

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{organizations.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actives</p>
              <p className="text-2xl font-bold text-green-600">
                {organizations.filter(o => o.is_active).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Inactives</p>
              <p className="text-2xl font-bold text-red-600">
                {organizations.filter(o => !o.is_active).length}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Résultats</p>
              <p className="text-2xl font-bold text-gray-900">{filteredOrganizations.length}</p>
            </div>
            <div className="p-3 bg-entrea-panel rounded-lg">
              <svg className="w-6 h-6 text-entrea-royal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher par nom, sous-domaine, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">Inactives</option>
            </select>
            <button
              onClick={loadOrganizations}
              className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition"
            >
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Liste des organisations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {listTotal > 0 ? renderOrgPagination('top') : null}
        {paginatedOrganizations.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p className="text-gray-600 text-lg">Aucune organisation trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-entrea-panel to-entrea-surface">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Organisation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Sous-domaine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Date de création
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedOrganizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-entrea-ink to-entrea-royal rounded-lg flex items-center justify-center text-white font-bold">
                          {org.name?.charAt(0).toUpperCase() || 'O'}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{org.name}</div>
                          {org.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {org.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        {org.subdomain}.mobile.uruganda.app
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{org.email || '-'}</div>
                      {org.phone && (
                        <div className="text-sm text-gray-500">{org.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {org.created_at ? new Date(org.created_at).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        org.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {org.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`/saas/organizations/${org.id}`)}
                          className="text-entrea-royal hover:text-entrea-navy transition"
                          title="Voir les détails"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleToggleStatus(org.id, org.is_active, org.name)}
                          className={`${
                            org.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                          } transition`}
                          title={org.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {org.is_active ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(org.id, org.name)}
                          className="text-red-600 hover:text-red-900 transition"
                          title="Supprimer"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {listTotal > 0 ? renderOrgPagination('bottom') : null}
      </div>
    </div>
  );
};

export default OrganizationsList;

